import React, { useEffect, useMemo } from 'react'

import styled from 'styled-components'
import HeroTitle from '../HeroLivePlayer/components/heroTitle'

import { Location } from '@reach/router'
import { getURIParamValue } from 'src/modules/utils/analytics'
import { defaultCTAURI } from 'src/modules/utils/constants'
import CTAButton from 'src/components/common/CTAButtonNew'
import { useSelector } from 'react-redux'
import { selectCTAParams } from 'src/modules/reducers/params'
import { createUrl } from 'src/modules/utils/createUrl'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import { getImageName } from 'src/modules/utils/files'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { useCtaText } from 'src/modules/hooks/useCtaText'
import { colors, mediaQuery, spacing } from 'src/constants/marketing-ds'

const baseVideoSrcPath = 'https://assets.fubo.tv/marketing/lp/videos/compressed/'

const HeroVideoPlayer = (props) => {
  const {
    location,
    title,
    subTitle,
    ctaText,
    background = 'https://assets-imgx.fubo.tv/marketing/lp/images/posters/live_preview_bg-2.jpg?w=1920&ch=width&auto=format,compress',
    logo = 'https://assets-imgx.fubo.tv/marketing/lp/images/logos/Fubo_Primary_Logo_FC_RGB.png?w=300',
    logoHeight,
    video,
    lang,
    videoUrlBase,
    market,
    disableFreeTrial = false,
  } = props
  // get ctaParams from redux
  const parsedParams = useSelector(selectCTAParams)
  let ctaURL = createUrl(defaultCTAURI, parsedParams)

  const urlClip = getURIParamValue(location.search, 'clip')
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const ctaCopy = useCtaText({ lang, market, disableFreeTrial, ctaText })
  const handleOnClick = () => {
    fireUIInteractionEvent(
      {
        element: ctaCopy,
        component: 'hero_section_cta_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  ctaURL = replaceUrlParam(ctaURL, 'comp', 'hero')

  const videoRef = React.useRef(null)

  const videoSource = useMemo(() => {
    if (urlClip != null) {
      if (videoUrlBase) {
        return videoUrlBase + urlClip
      }
      return urlClip
    }
    return video
  }, [urlClip, video, videoUrlBase])

  const firstFrameImg = useMemo(() => {
    if (videoUrlBase && urlClip) {
      return videoUrlBase + getImageName(urlClip) + '?ch=width&auto=format,compress'
    } else if (video) {
      return baseVideoSrcPath + getImageName(video) + '?ch=width&auto=format,compress'
    }
    return ''
  }, [videoUrlBase, urlClip, video])

  // Handle video source and poster changes
  useEffect(() => {
    const videoElement = videoRef.current
    if (videoElement && videoSource != null) {
      try {
        // Update the source directly and reload
        const source = videoElement.querySelector('source')
        if (source) {
          source.src = videoSource
        }
        // Update poster if firstFrameImg is available
        if (firstFrameImg) {
          videoElement.poster = firstFrameImg
        }
        videoElement.load()

        // Add error handling
        videoElement.onerror = () => {
          console.error('Error loading video:', videoSource)
        }

        // Add load event to confirm video loaded successfully
        videoElement.onloadeddata = () => {
          console.log('Video loaded successfully:', videoSource)
        }
      } catch (error) {
        console.error('Error updating video:', error)
      }
    }
  }, [videoSource])

  // Handle poster changes separately
  useEffect(() => {
    const videoElement = videoRef.current
    if (videoElement && firstFrameImg) {
      try {
        videoElement.poster = firstFrameImg
      } catch (error) {
        console.error('Error updating poster:', error)
      }
    }
  }, [firstFrameImg])

  return (
    <Location>
      {({ location }) => (
        <StyledHeroVideoPlayer backgroundImage={background} logoHeight={logoHeight}>
          <div className="logo-wrapper">
            <img src={logo} alt="Fubo Logo" width="100%" />
          </div>
          <div className="hero-content">
            <HeroTitle title={title} subtitle={subTitle} location={location} />
            <div className="hero-video-container">
              <video
                ref={videoRef}
                muted
                controls
                playsInline
                autoPlay={true}
                crossOrigin
                poster={firstFrameImg}
              >
                <source src={videoSource} type="video/mp4" />
              </video>
            </div>
            <CTAButton link={ctaURL} onClick={handleOnClick}>
              {ctaCopy}
            </CTAButton>
          </div>
        </StyledHeroVideoPlayer>
      )}
    </Location>
  )
}

export default HeroVideoPlayer

const StyledHeroVideoPlayer = styled.div`
  background: ${({ backgroundImage }) =>
    backgroundImage ? `url(${backgroundImage})` : colors.gradients.midnight.medium};
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  box-sizing: border-box;
  color: #fff;
  width: 100%;
  overflow: hidden;

  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${spacing.xl} ${spacing.xs};

  ${mediaQuery.tablet} {
    padding: ${spacing.xxxl} ${spacing.lg};
    min-height: 600px;
  }

  ${mediaQuery.desktop} {
    padding: ${spacing.xxxl} ${spacing.xl};
  }

  .logo-wrapper {
    position: relative;
    width: auto;
    margin: 0 auto ${spacing.lg};
    box-sizing: border-box;

    img {
      max-width: 100%;
      max-height: ${(props) => props.logoHeight || '48px'};
      height: 100%;
      width: auto;
    }

    @media (max-width: 768px) {
      text-align: center;
      max-height: ${(props) => props.logoHeight || '48px'};
    }

    ${mediaQuery.tablet} {
      margin-bottom: ${spacing.xl};
    }

    @media (min-width: 1300px) {
      position: absolute;
      top: ${spacing.xxxl};
      left: ${spacing.xl};
    }
  }

  .hero-content {
    width: 100%;
    text-align: center;
    position: relative;
    box-sizing: border-box;
    .hero-video-container {
      width: 100%;
      max-width: 900px;
      position: relative;
      margin: 0 auto ${spacing.xl};

      video {
        width: 100%;
        max-width: 900px;
      }
    }
  }
`
